import { Module } from 'vuex';
import { PriceState, RootState } from '../types';
import { api } from '../../plugins/salut-api';
import { ContactTypeCode, Plan, TableItemsOptions } from '../../types';
import Vue from 'vue';

export const PriceActionKeys = {
	loadPlans: 'loadPlans',
	loadPlanTableData: 'loadPlanTableData',
	loadPlan: 'loadPlan',
	loadStripeProducts: 'loadStripeProducts',
	deletePlan: 'deletePlan',
};

export const PriceGetterKeys = {
	getPlanById: 'getPlanById',
	getPlanByIds: 'getPlanByIds',
	getPlansByProfileTypeId: 'getPlansByProfileTypeId',
	plans: 'plans',
	planTableData: 'planTableData',
	stripeProducts: 'stripeProducts',
	loading: 'loading',
};

const SET_PLANS = 'SET_PLANS';
const SET_PLAN_TABLE_DATA = 'SET_PLAN_TABLE_DATA';
const ADD_TO_PLANS = 'ADD_TO_PLANS';
const SET_STRIPE_PRODUCTS = 'SET_STRIPE_PRODUCTS';
const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';

const store: Module<PriceState, RootState> = {
	namespaced: true,
	state: {
		loading: 0,
		plans: [],
		planTableData: {
			items: {
				data: [],
			},
			total: 0,
		},
		stripeProductTableData: {
			items: [],
			total: 0,
		},
	},
	getters: {
		[PriceGetterKeys.getPlanById]: state => (planId: string) =>
			planId ? state.plans.find(plan => plan.id === planId) : null,
		[PriceGetterKeys.getPlanByIds]: state => (planIds: string[]) =>
			state.plans.filter(plan => planIds.includes(plan.id)),
		[PriceGetterKeys.getPlansByProfileTypeId]: (state, getters) => (
			profileTypeId: number,
		) =>
			getters.plans.filter(
				(plan: Plan) => plan.subCategory === String(profileTypeId),
			),

		[PriceGetterKeys.plans]: state => state.plans,
		[PriceGetterKeys.planTableData]: state => state.planTableData,
		[PriceGetterKeys.stripeProducts]: state => state.stripeProductTableData,
		[PriceGetterKeys.loading]: state => Boolean(state.loading),
	},
	actions: {
		async [PriceActionKeys.loadPlans](
			{ commit },
			data?: { contactType?: ContactTypeCode; profileTypeId?: number },
		) {
			try {
				commit(SHOW_LOADING);
				const { data: plans } = await api.fetchPlans(
					data?.contactType,
					data?.profileTypeId ? String(data?.profileTypeId) : undefined,
				);
				commit(SET_PLANS, plans);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PriceActionKeys.loadPlanTableData](
			{ commit },
			options: TableItemsOptions,
		) {
			try {
				commit(SHOW_LOADING);
				const { result, total } = await api.fetchPaginatedPlans(options);
				commit(SET_PLAN_TABLE_DATA, { result, total });
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PriceActionKeys.loadPlan](
			{ getters, commit },
			{ planId }: { planId: string },
		) {
			try {
				commit(SHOW_LOADING);
				const plan = await api.fetchPlan(planId);
				commit(ADD_TO_PLANS, plan);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PriceActionKeys.loadStripeProducts](
			{ commit },
			params: { options: TableItemsOptions },
		) {
			try {
				commit(SHOW_LOADING);
				const { result, total } = await api.fetchStripeProducts(params.options);
				commit(SET_STRIPE_PRODUCTS, { result, total });
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PriceActionKeys.deletePlan](
			{ commit },
			{ planId }: { planId: string },
		) {
			await api.deletePlan(planId);
		},
	},
	mutations: {
		[SET_STRIPE_PRODUCTS](state, { result, total }) {
			state.stripeProductTableData = {
				items: result,
				total: total,
			};
		},
		[SET_PLANS](state, plans) {
			state.plans = plans;
		},
		[SET_PLAN_TABLE_DATA](state, { result, total }) {
			state.planTableData = {
				items: result,
				total: total,
			};
		},
		[ADD_TO_PLANS](state, newPlan: Plan) {
			const indexOfExistingPlan = state.plans.findIndex(
				plan => plan.id === newPlan.id,
			);
			if (indexOfExistingPlan > -1)
				Vue.delete(state.plans, indexOfExistingPlan);
			state.plans.push(newPlan);
		},
		[SHOW_LOADING](state) {
			state.loading += 1;
		},

		[HIDE_LOADING](state) {
			state.loading -= 1;
		},
	},
};

export default store;
